
import {
  AgeTagType,
  FontItem,
  SentenceItem,
  SurveyCsvData,
} from "@/store/types";
import { Options, Vue } from "vue-class-component";
import CompareFonts from "@/components/Survey/whichFont/CompareFonts.vue";

import { namespace } from "vuex-class";
import router from "@/router";

import { RTWebFontAPI } from "@/utils/realtype-web-font";

import { sheffeAnovaOnPairedComparisonV2 } from "./../../sheffeAnovaOnPairedComparison";
import { Watch } from "vue-property-decorator";
import { arrayShuffle, getRandomInt } from "@/utils/util";

declare global {
  interface Window {
    RT: RTWebFontAPI;
  }
}

const SystemModule = namespace("system");
const SurveyModule = namespace("survey");

@Options({
  components: {
    "compare-fonts": CompareFonts,
  },
  beforeRouteEnter: (to, from, next) => {
    if (from.fullPath !== "/survey/which-layout") {
      router.replace("/survey/entrance");
    } else {
      next();
    }
  },
})
export default class SurveyWhichFont extends Vue {
  public isVisible = false;

  public isFadeIn = false;
  public isFadeOut = false;

  public isChildVisible = true;

  public async pageFadeOut() {
    this.isFadeIn = false;
    await this.updateIsWhiteout(true);
    this.isFadeOut = true;
  }

  public async pageFadeIn() {
    this.isFadeOut = false;
    this.isFadeIn = true;
  }

  public async mounted(): Promise<void> {
    this.updateIsWhiteout(false);
    window.scrollTo({ top: 0 });
    this.loadCombination(Number(this.$route.params.id));
    await this.setSentenceTable();
    this.isVisible = true;
    this.pageFadeIn();
  }

  @SurveyModule.Getter("byAgeTag")
  private byAgeTag!: AgeTagType;

  @SurveyModule.Getter("surveyData")
  private surveyData!: SurveyCsvData;

  @SystemModule.Getter("getFontList")
  private getFontList!: FontItem[];

  @SurveyModule.Getter("isWhiteout")
  private isWhiteout!: boolean;

  @SurveyModule.Getter("surveySentenceList")
  private surveySentenceList!: SentenceItem[];

  @SurveyModule.Action("updateSurveyFont")
  private updateSurveyFont!: (font: number) => Promise<void>;

  @SurveyModule.Action("updateSurveyFontCompareResult")
  private updateSurveyFontCompareResult!: (
    compareScore: number[][]
  ) => Promise<void>;

  @SurveyModule.Action("updateIsWhiteout")
  private updateIsWhiteout!: (isWhiteout: boolean) => Promise<void>;

  @SystemModule.Action("updateLocalFontId")
  private updateLocalFontId!: (localFontId: number) => Promise<void>;

  @Watch("compareId")
  public async onChangeCompareId(value: number): Promise<void> {
    if (value < 21) {
      await this.loadCombination(value);
    }
  }

  public sentenceTable: SentenceItem[] = [];

  public async setSentenceTable() {
    this.sentenceTable = this.surveySentenceList.filter(
      (item) => item.layoutId === this.surveyData.layout
    );
    // this.sentenceTable = arrayShuffle(this.sentenceTable);
  }

  private get surveySentence(): string {
    let sentence = "";

    const index = this.compareId % this.sentenceTable.length;
    if (this.sentenceTable[index]) {
      sentence = this.sentenceTable[index].text;
    }
    return sentence;
  }

  private get compareId(): number {
    let id = 0;

    if (this.$route.params.id) {
      id = Number(this.$route.params.id);
    }
    return id;
  }

  public async nextPage(font: number): Promise<void> {
    await this.updateSurveyFont(font);
    await this.updateLocalFontId(font);
    await this.pageFadeOut();
    await this.$router.push("/survey/courtyard");
  }

  public subjectValue = 7;

  public fontComparedItemIndexesTmp: number[][] = [];

  private get currentComparePair(): FontItem[] {
    let pair: FontItem[] = [];

    if (this.fontComparedItemIndexesTmp[this.compareId]) {
      for (const fontIndex of this.fontComparedItemIndexesTmp[this.compareId]) {
        const fontItem = this.getFontList.find((item, i) => i === fontIndex);
        if (fontItem) {
          pair.push(fontItem);
        }
      }
    }
    return pair;
  }

  @Watch("compareFontItems")
  public async onChange() {
    await this.updateIsWhiteout(true);
    this.isChildVisible = false;
    await this.updateIsWhiteout(false);
    this.isChildVisible = true;
    window.scrollTo({ top: 0 });
  }

  public get compareFontItems(): FontItem[] {
    let compareFontItemsTmp: FontItem[] = [];

    const compareFontIndexPair =
      this.fontComparedItemIndexesTmp[this.compareId];
    if (compareFontIndexPair) {
      for (const fontIndex of compareFontIndexPair) {
        const fontItem = this.getFontList.find(
          (item, index) => index === fontIndex
        );
        if (fontItem) {
          compareFontItemsTmp.push(fontItem);
        }
      }
    }

    return compareFontItemsTmp;
  }

  private get fontComparedItemIndexes(): number[][] {
    const defaultItemIndexes = [
      [0, 0],
      [1, 1],
      [2, 2],
      [3, 3],
      [4, 4],
      [5, 5],
      [6, 6],
    ];
    return defaultItemIndexes.concat(this.fontComparedItemIndexesTmp);
  }

  public selectedFontItem: FontItem[] = [];
  public voteCount = 0;

  public async vote(id: number): Promise<void> {
    const itemIndex = this.getFontList.findIndex((item) => item.id === id);

    const anotherItem = this.currentComparePair.find((item) => item.id !== id);
    if (!anotherItem) {
      return;
    }
    const anotherItemIndex = this.getFontList.findIndex(
      (item) => item.id === anotherItem.id
    );

    this.score[itemIndex][anotherItemIndex] = 1;
    this.score[anotherItemIndex][itemIndex] = -1;

    if (process.env.VUE_APP_IS_DEBUG_CONSOLE === "TRUE") {
      console.log(JSON.stringify(this.score));
    }

    if (this.compareId >= 0 && this.compareId < 20) {
      this.voteCount = this.compareId + 1;
      await this.loadCombination(this.voteCount);
      await this.$router.push(`/survey/which-font/${this.voteCount}`);
    } else {
      await this.aggregateScores();
    }
  }

  public async reVote(id: number): Promise<void> {
    await this.nextPage(id);
  }

  public maxPairCombinationLength =
    (Math.pow(this.subjectValue, 2) + this.subjectValue) / 2;

  /**
   * スコアの集計
   */
  public async aggregateScores(): Promise<void> {
    /**
     * 三竦みの場合のスコア
     */
    // this.score = [
    //   [0,1,-1,1,1,-1,1],
    //   [-1,0,1,-1,1,1,1],
    //   [1,-1,0,1,1,1,-1],
    //   [-1,1,-1,0,1,1,-1],
    //   [-1,-1,-1,-1,0,-1,1],
    //   [1,-1,-1,-1,1,0,1],
    //   [-1,-1,1,1,-1,-1,0]
    // ]

    if (process.env.VUE_APP_IS_DEBUG_CONSOLE === "TRUE") {
      console.log(JSON.stringify(this.score));
    }
    const result = [];
    for (let r = 0; r <= this.subjectValue - 1; r++) {
      result.push(
        this.score[r].reduce(
          (previousValue, currentValue) => previousValue + currentValue,
          0
        )
      );
    }

    if (process.env.VUE_APP_IS_DEBUG_CONSOLE === "TRUE") {
      console.log("result", result);
    }

    let topScore = 0;
    let topItems = [];

    for (const s of result) {
      if (s >= topScore) {
        topScore = s;
      }
    }

    for (let s = 0; s < result.length; s++) {
      if (result[s] === topScore) {
        topItems.push(this.getFontList[s]);
      }
    }

    const res = await sheffeAnovaOnPairedComparisonV2(this.score);

    const significants = [];
    for (const content of res) {
      significants.push(
        content.reduce(
          (previousValue, currentValue) => previousValue + currentValue,
          0
        )
      );
    }

    if (process.env.VUE_APP_IS_DEBUG_CONSOLE === "TRUE") {
      console.log("topScore", topScore);
      console.log("topItems", JSON.stringify(topItems));
      console.log("優位差", res);
      console.log("優位差の総和", significants);
    }

    /**
     * ひとまず優位差の総和から順位付け
     */
    let topSignificantValue = 0;
    let topSignificantItems = [];
    for (const s of significants) {
      if (s > topSignificantValue) {
        topSignificantValue = s;
      }
    }

    for (let s = 0; s < result.length; s++) {
      if (result[s] === topScore) {
        topSignificantItems.push(this.getFontList[s]);
      }
    }

    this.selectedFontFamilyTag = topSignificantItems[0].tag;
    await this.updateSurveyFontCompareResult(this.score);

    /**
     * 同票一位が複数ある
     */
    if (topSignificantItems.length > 1) {
      this.selectedFontItem = topSignificantItems;
      await this.$router.push(`/survey/which-font/${this.voteCount + 1}`);
    } else {
      await this.nextPage(topSignificantItems[0].id);
    }
  }

  /**
   * 比較するペアを作成
   * 最大21個(7C2)のペアを作成する
   */
  public async loadCombination(index: number): Promise<void> {
    if (
      this.fontComparedItemIndexes.length < this.maxPairCombinationLength &&
      !this.fontComparedItemIndexesTmp[index]
    ) {
      let itemA = 0;
      let itemB = 0;

      do {
        itemA = getRandomInt(0, 6);
        itemB = getRandomInt(0, 6);
      } while (
        this.fontComparedItemIndexes.some((item) => {
          return (
            (item[0] === itemA && item[1] === itemB) ||
            (item[0] === itemB && item[1] === itemA)
          );
        }) &&
        this.fontComparedItemIndexes.length < this.maxPairCombinationLength
      );
      this.fontComparedItemIndexesTmp[index] = [itemA, itemB];
    }
  }

  public selectedFontFamilyTag = "";

  public score = [
    [0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0],
  ];

  public get isHiraganaWord() {
    return this.byAgeTag === "UNDER_10";
  }

  public get titleWord(): string {
    let word = "読みやすい文字はどちらですか？";
    if (this.isHiraganaWord) {
      word = "よみやすい もじは どちらですか？";
    }

    if (
      this.selectedFontItem.length > 2 &&
      Number(this.$route.params.id) === 21
    ) {
      word = "読みやすい文字はどれですか？";
      if (this.isHiraganaWord) {
        word = "いちばん よみやすい もじは どれですか？";
      }
    }

    return word;
  }
}
